import React, { useState, useEffect, useCallback, useRef } from 'react';
import SecureVideo from '../learning/SecureVideo';
import {
  buildPlaybackStreamUrl,
  resolvePlaybackFormat,
  type PlaybackFormat,
} from '../../hooks/useSecurePlaybackStream';
import {
  classroomLifecycleLabel,
  formatDbDateTimeVi,
  formatDbDateVi,
  resolveClassroomLifecycleStatus,
  toDatetimeLocalValue,
  toDbDateTimePayload,
} from '../../utils/dbDateDisplay';

// ─────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────
interface ScheduleItem {
  dayOfWeek: number; // 1=Mon...7=Sun
  startHour: string; // "19:00"
  endHour: string;   // "21:00"
}

interface Classroom {
  id: number;
  courseId: number;
  name: string;
  code: string | null;
  startDate: string | null;
  endDate: string | null;
  startEnrollmentDate: string | null;
  totalSessions: number | null;
  scheduleItems: ScheduleItem[] | string | null;
  fee: number | null;
  totalSlot: number | null;
  totalRegistered: number;
  status: string;
  isActive: boolean;
  zaloLink: string | null;
  address: string | null;
  googleMeetLink?: string | null;
  viewFromClassroomId: number | null;
  viewFromClassroomName: string | null;
  canDelete?: boolean;
  activeRegistrationCount?: number;
  courseName?: string | null;
  courseCode?: string | null;
  courseMethod?: string | null;
}

interface ClassroomStudent {
  registerId: number;
  registerCode: string | null;
  status: string;
  fee: number | null;
  registerAt: string | null;
  approvedAt: string | null;
  userId: number;
  fullName: string | null;
  email: string | null;
  phone: string | null;
  studentId: number | null;
  studentStatus: string | null;
}

interface LiveSession {
  id: number;
  title: string;
  startTime: string;
  endTime: string;
  meetLink: string | null;
  recordingUrl: string | null;
  recordingStatus: string;
  status: string;
  sessionId?: number | null;
  sessionTitle: string | null;
  sessionOrderIndex: number | null;
  materialCount?: number;
}

interface SessionMaterial {
  id: number;
  title: string;
  type?: string;
  fileUrl: string;
  fileType?: string;
  sessionId?: number | null;
  sessionTitle?: string | null;
  sessionOrderIndex?: number | null;
}

interface CourseCatalogMaterial {
  id: number;
  title: string;
  type?: string;
  fileUrl: string;
  fileType?: string;
  sessionId?: number | null;
  sessionTitle?: string | null;
  sessionOrderIndex?: number | null;
}

type DetailTab = 'info' | 'students' | 'sessions';

interface Props {
  courseId: number;
}

const DAY_LABELS: Record<number, string> = {
  1: 'Thứ 2', 2: 'Thứ 3', 3: 'Thứ 4', 4: 'Thứ 5',
  5: 'Thứ 6', 6: 'Thứ 7', 7: 'Chủ nhật'
};

const STATUS_COLORS: Record<string, string> = {
  UPCOMING: 'bg-m2-primary/15 text-m2-primary',
  ACTIVE: 'bg-m2-secondary/15 text-green-700',
  LEARNING: 'bg-purple-100 text-purple-700',
  FINISH: 'bg-m2-surface-high text-m2-on-surface-variant',
  STOP: 'bg-orange-100 text-orange-700',
  CANCELLED: 'bg-red-100 text-red-700',
};

function classroomStatusMeta(c: {
  startDate: string | null;
  endDate: string | null;
  status: string;
}) {
  const code = resolveClassroomLifecycleStatus(c.startDate, c.endDate, c.status);
  return {
    code,
    label: classroomLifecycleLabel(c.startDate, c.endDate, c.status),
    color: STATUS_COLORS[code] || 'bg-m2-surface-high text-m2-on-surface-variant',
  };
}

const LS_STATUS_COLORS: Record<string, string> = {
  SCHEDULED: 'bg-m2-primary/10 text-m2-primary',
  IN_PROGRESS: 'bg-m2-error/10 text-red-700',
  COMPLETED: 'bg-m2-surface-high text-m2-on-surface-variant',
  CANCELLED: 'bg-orange-500/10 text-orange-700',
};

const REG_STATUS_COLORS: Record<string, string> = {
  WAIT_PAYMENT: 'bg-amber-100 text-amber-800',
  SUCCESS: 'bg-m2-secondary/15 text-green-700',
  APPROVED: 'bg-m2-secondary/15 text-green-700',
  LEARNING: 'bg-purple-100 text-purple-700',
  COMPLETED: 'bg-m2-surface-high text-m2-on-surface-variant',
  CANCEL: 'bg-red-100 text-red-700',
  CANCELLED: 'bg-red-100 text-red-700',
};

function formatMoney(n: number | null | undefined) {
  if (n == null) return '—';
  return new Intl.NumberFormat('vi-VN').format(n) + ' ₫';
}

function isCancelledReg(status: string | null | undefined) {
  const s = String(status || '').toUpperCase().trim();
  return s === 'CANCEL' || s === 'CANCELLED';
}

const emptySchedule: ScheduleItem = { dayOfWeek: 2, startHour: '19:00', endHour: '21:00' };

const emptyForm = {
  name: '',
  code: '',
  startDate: '',
  startEnrollmentDate: '',
  totalSessions: 12,
  fee: '',
  totalSlot: '',
  zaloLink: '',
  address: '',
  viewFromClassroomId: '',
  scheduleItems: [{ ...emptySchedule }] as ScheduleItem[],
};

function parseSchedule(raw: any): ScheduleItem[] {
  if (!raw) return [];
  if (typeof raw === 'string') {
    try { return JSON.parse(raw); } catch { return []; }
  }
  return raw as ScheduleItem[];
}

function formatDate(iso: string | null) {
  if (!iso) return '—';
  const d = formatDbDateVi(iso);
  return d === 'Chưa có thông tin' ? '—' : d;
}

function toDatetimeLocal(iso: string | null | undefined) {
  return toDatetimeLocalValue(iso);
}

// ─────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────
export const AdminClassroomManager: React.FC<Props> = ({ courseId }) => {
  const [classrooms, setClassrooms] = useState<Classroom[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Form state
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form, setForm] = useState({ ...emptyForm });

  // Sync Modal State
  const [syncModalSessionId, setSyncModalSessionId] = useState<number | null>(null);
  const [syncLink, setSyncLink] = useState('');
  const [isSyncing, setIsSyncing] = useState(false);

  // Detail view (full classroom detail)
  const [detailClassroomId, setDetailClassroomId] = useState<number | null>(null);
  const [detailClassroom, setDetailClassroom] = useState<Classroom | null>(null);
  const [detailTab, setDetailTab] = useState<DetailTab>('info');
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [students, setStudents] = useState<ClassroomStudent[]>([]);
  const [loadingStudents, setLoadingStudents] = useState(false);
  const [deletingClassroom, setDeletingClassroom] = useState(false);

  // Transfer classroom
  const [transferStudent, setTransferStudent] = useState<ClassroomStudent | null>(null);
  const [transferToId, setTransferToId] = useState<string>('');
  const [transferNote, setTransferNote] = useState('');
  const [transferring, setTransferring] = useState(false);

  // Live sessions per classroom (detail + legacy expand)
  const [expandedClassroom, setExpandedClassroom] = useState<number | null>(null);
  const [liveSessions, setLiveSessions] = useState<LiveSession[]>([]);
  const [loadingLS, setLoadingLS] = useState(false);

  // Edit live session (title + datetime)
  const [editingLsId, setEditingLsId] = useState<number | null>(null);
  const [editingLsTitle, setEditingLsTitle] = useState('');
  const [editingLsStart, setEditingLsStart] = useState('');
  const [editingLsEnd, setEditingLsEnd] = useState('');
  const [savingLs, setSavingLs] = useState(false);
  const [cancellingLsId, setCancellingLsId] = useState<number | null>(null);

  // Add live session form
  const [showAddLs, setShowAddLs] = useState(false);
  const [addingLs, setAddingLs] = useState(false);
  const [addLsForm, setAddLsForm] = useState({ title: '', startTime: '', endTime: '', autoSchedule: true });

  // Create Meet state
  const [creatingMeet, setCreatingMeet] = useState<number | null>(null);
  const [previewSession, setPreviewSession] = useState<LiveSession | null>(null);
  const [previewVideoUrl, setPreviewVideoUrl] = useState<string | null>(null);
  const [previewFormat, setPreviewFormat] = useState<PlaybackFormat>('mp4');
  const previewVideoRef = useRef<HTMLVideoElement | null>(null);

  // Assign BTVN to classroom
  const [hwModalClassroom, setHwModalClassroom] = useState<Classroom | null>(null);
  const [hwOptions, setHwOptions] = useState<Array<{ id: number; name: string; assigned: number }>>([]);
  const [hwSelectedIds, setHwSelectedIds] = useState<Set<number>>(new Set());
  const [hwLoading, setHwLoading] = useState(false);
  const [hwSaving, setHwSaving] = useState(false);

  // Attach materials to live session (chọn từ danh mục khóa — không upload mới)
  const [materialsLsId, setMaterialsLsId] = useState<number | null>(null);
  const [sessionMaterials, setSessionMaterials] = useState<SessionMaterial[]>([]);
  const [courseCatalogMaterials, setCourseCatalogMaterials] = useState<CourseCatalogMaterial[]>([]);
  const [loadingSessionMaterials, setLoadingSessionMaterials] = useState(false);
  const [linkingMaterialId, setLinkingMaterialId] = useState<number | null>(null);
  const [materialPickerFilter, setMaterialPickerFilter] = useState('');

  const getAuthHeader = () => ({ Authorization: `Bearer ${localStorage.getItem('accessToken')}` });

  // ── Fetch classrooms ──────────────────────────
  const fetchClassrooms = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`/api/v1/admin/courses/${courseId}/classrooms`, { headers: getAuthHeader() });
      const data = await res.json();
      if (data.success) setClassrooms(data.data);
      else setError(data.message || 'Lỗi khi tải danh sách lớp học');
    } catch {
      setError('Không thể kết nối server');
    } finally {
      setLoading(false);
    }
  }, [courseId]);

  const openSyncModal = (sessionId: number) => {
    setSyncModalSessionId(sessionId);
    setSyncLink('');
  };

  const handleSyncSubmit = async () => {
    if (syncModalSessionId === null) return;
    setIsSyncing(true);
    try {
      const res = await fetch(`/api/v1/admin/live-sessions/${syncModalSessionId}/sync-recording`, {
        method: 'POST',
        headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ link: syncLink.trim() || undefined })
      });
      const data = await res.json();
      if (data.success) {
        alert(data.message || 'Tiến trình đang xử lý...');
        setSyncModalSessionId(null);
        setSyncLink('');
      } else {
        alert(data.message || 'Có lỗi xảy ra');
      }
    } catch (error) {
      console.error('Sync failed', error);
      alert('Lỗi kết nối server');
    } finally {
      setIsSyncing(false);
    }
  };

  useEffect(() => {
    fetchClassrooms();
  }, [fetchClassrooms]);

  // ── Fetch live sessions of a classroom ────────
  const fetchLiveSessions = async (classroomId: number) => {
    setLoadingLS(true);
    try {
      const res = await fetch(`/api/v1/admin/classrooms/${classroomId}/live-sessions`, { headers: getAuthHeader() });
      const data = await res.json();
      if (data.success) setLiveSessions(data.data);
    } catch { /* ignore */ }
    finally { setLoadingLS(false); }
  };

  const fetchSessionMaterials = async (liveSessionId: number) => {
    setLoadingSessionMaterials(true);
    try {
      const res = await fetch(`/api/v1/admin/live-sessions/${liveSessionId}/materials`, {
        headers: getAuthHeader(),
      });
      const data = await res.json();
      if (data.success) setSessionMaterials(data.data || []);
      else setSessionMaterials([]);
    } catch {
      setSessionMaterials([]);
    } finally {
      setLoadingSessionMaterials(false);
    }
  };

  const fetchCourseCatalogMaterials = async () => {
    try {
      const res = await fetch(`/api/v1/admin/courses/${courseId}/materials`, {
        headers: getAuthHeader(),
      });
      const data = await res.json();
      if (data.success) setCourseCatalogMaterials(data.data || []);
      else setCourseCatalogMaterials([]);
    } catch {
      setCourseCatalogMaterials([]);
    }
  };

  const toggleMaterialsPanel = async (liveSessionId: number) => {
    if (materialsLsId === liveSessionId) {
      setMaterialsLsId(null);
      setSessionMaterials([]);
      setMaterialPickerFilter('');
      return;
    }
    setMaterialsLsId(liveSessionId);
    setMaterialPickerFilter('');
    await Promise.all([fetchSessionMaterials(liveSessionId), fetchCourseCatalogMaterials()]);
  };

  const handleLinkMaterial = async (liveSessionId: number, materialId: number) => {
    setLinkingMaterialId(materialId);
    try {
      const res = await fetch(`/api/v1/admin/live-sessions/${liveSessionId}/materials/link`, {
        method: 'POST',
        headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ materialIds: [materialId] }),
      });
      const data = await res.json();
      if (data.success) {
        const mats = data.data?.materials || [];
        setSessionMaterials(mats);
        await fetchCourseCatalogMaterials();
        setLiveSessions((prev) =>
          prev.map((ls) =>
            ls.id === liveSessionId
              ? {
                  ...ls,
                  materialCount: mats.length,
                  sessionId: data.data?.sessionId ?? ls.sessionId,
                }
              : ls
          )
        );
      } else {
        alert(data.message || 'Gắn tài liệu thất bại');
      }
    } catch {
      alert('Lỗi kết nối server');
    } finally {
      setLinkingMaterialId(null);
    }
  };

  const handleUnlinkMaterial = async (liveSessionId: number, materialId: number) => {
    setLinkingMaterialId(materialId);
    try {
      const res = await fetch(`/api/v1/admin/live-sessions/${liveSessionId}/materials/unlink`, {
        method: 'POST',
        headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ materialIds: [materialId] }),
      });
      const data = await res.json();
      if (data.success) {
        const mats = data.data?.materials || [];
        setSessionMaterials(mats);
        await fetchCourseCatalogMaterials();
        setLiveSessions((prev) =>
          prev.map((ls) =>
            ls.id === liveSessionId ? { ...ls, materialCount: mats.length } : ls
          )
        );
      } else {
        alert(data.message || 'Gỡ tài liệu thất bại');
      }
    } catch {
      alert('Lỗi kết nối server');
    } finally {
      setLinkingMaterialId(null);
    }
  };

  const fetchStudents = async (classroomId: number) => {
    setLoadingStudents(true);
    try {
      const res = await fetch(`/api/v1/admin/classrooms/${classroomId}/students`, { headers: getAuthHeader() });
      const data = await res.json();
      if (data.success) setStudents(data.data || []);
      else setStudents([]);
    } catch {
      setStudents([]);
    } finally {
      setLoadingStudents(false);
    }
  };

  const openTransferModal = (st: ClassroomStudent) => {
    setTransferStudent(st);
    setTransferToId('');
    setTransferNote('');
  };

  const closeTransferModal = () => {
    if (transferring) return;
    setTransferStudent(null);
    setTransferToId('');
    setTransferNote('');
  };

  const handleTransferClassroom = async () => {
    if (!transferStudent || !transferToId) return;
    const toId = Number(transferToId);
    if (!Number.isFinite(toId) || toId <= 0) {
      alert('Vui lòng chọn lớp đích');
      return;
    }
    if (!window.confirm(`Chuyển học viên "${transferStudent.fullName || transferStudent.email}" sang lớp đã chọn?`)) {
      return;
    }

    setTransferring(true);
    try {
      const res = await fetch(`/api/v1/admin/registers/${transferStudent.registerId}/transfer-classroom`, {
        method: 'POST',
        headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
        body: JSON.stringify({
          toClassroomId: toId,
          note: transferNote.trim() || undefined,
        }),
      });
      const data = await res.json();
      if (!data.success) {
        alert(data.message || 'Chuyển lớp thất bại');
        return;
      }
      alert(data.message || 'Đã chuyển lớp thành công');
      setTransferStudent(null);
      setTransferToId('');
      setTransferNote('');
      if (detailClassroomId) {
        await Promise.all([fetchStudents(detailClassroomId), fetchClassrooms()]);
        // refresh detail counts
        const detailRes = await fetch(`/api/v1/admin/classrooms/${detailClassroomId}`, {
          headers: getAuthHeader(),
        });
        const detailData = await detailRes.json();
        if (detailData.success) setDetailClassroom(detailData.data);
      }
    } catch {
      alert('Lỗi kết nối server');
    } finally {
      setTransferring(false);
    }
  };

  const openDetail = async (classroomId: number, tab: DetailTab = 'info') => {
    setDetailClassroomId(classroomId);
    setDetailTab(tab);
    setExpandedClassroom(null);
    setEditingLsId(null);
    setShowAddLs(false);
    setLoadingDetail(true);
    setError(null);
    try {
      const res = await fetch(`/api/v1/admin/classrooms/${classroomId}`, { headers: getAuthHeader() });
      const data = await res.json();
      if (data.success) {
        setDetailClassroom(data.data);
        // keep list row in sync if present
        setClassrooms(prev =>
          prev.map(c => (c.id === classroomId ? { ...c, ...data.data, totalRegistered: data.data.totalRegistered ?? c.totalRegistered } : c))
        );
      } else {
        setError(data.message || 'Không tải được chi tiết lớp');
        setDetailClassroomId(null);
        setDetailClassroom(null);
        return;
      }
      await Promise.all([fetchLiveSessions(classroomId), fetchStudents(classroomId)]);
    } catch {
      setError('Không thể kết nối server');
      setDetailClassroomId(null);
      setDetailClassroom(null);
    } finally {
      setLoadingDetail(false);
    }
  };

  const closeDetail = () => {
    setDetailClassroomId(null);
    setDetailClassroom(null);
    setStudents([]);
    setLiveSessions([]);
    setEditingLsId(null);
    setShowAddLs(false);
    setMaterialsLsId(null);
    setSessionMaterials([]);
    setCourseCatalogMaterials([]);
    setMaterialPickerFilter('');
    setDetailTab('info');
  };

  const refreshDetail = async (classroomId: number) => {
    try {
      const res = await fetch(`/api/v1/admin/classrooms/${classroomId}`, { headers: getAuthHeader() });
      const data = await res.json();
      if (data.success) {
        setDetailClassroom(data.data);
        setClassrooms(prev =>
          prev.map(c => (c.id === classroomId ? { ...c, ...data.data, totalRegistered: data.data.totalRegistered ?? c.totalRegistered } : c))
        );
      }
    } catch { /* ignore */ }
    await Promise.all([fetchLiveSessions(classroomId), fetchStudents(classroomId)]);
  };

  const handleDeleteClassroom = async (classroom: Classroom) => {
    const registered = classroom.totalRegistered ?? classroom.activeRegistrationCount ?? 0;
    if (registered > 0 || classroom.canDelete === false) {
      alert('Không thể xóa lớp đã có học viên đăng ký.');
      return;
    }
    const ok = window.confirm(
      `Xác nhận xóa lớp "${classroom.name}"?\n\n` +
      `Chỉ xóa được khi chưa có ai đăng ký.\n` +
      `Các buổi học (live sessions) của lớp cũng sẽ bị xóa.`
    );
    if (!ok) return;

    setDeletingClassroom(true);
    setError(null);
    try {
      const res = await fetch(`/api/v1/admin/classrooms/${classroom.id}`, {
        method: 'DELETE',
        headers: getAuthHeader(),
      });
      const data = await res.json();
      if (data.success) {
        await fetchClassrooms();
        if (detailClassroomId === classroom.id) closeDetail();
        alert(data.message || 'Đã xóa lớp học');
      } else {
        setError(data.message || 'Xóa lớp thất bại');
        alert(data.message || 'Xóa lớp thất bại');
      }
    } catch {
      setError('Không thể kết nối server');
      alert('Lỗi kết nối server');
    } finally {
      setDeletingClassroom(false);
    }
  };

  const startEditLs = (ls: LiveSession) => {
    setEditingLsId(ls.id);
    setEditingLsTitle(ls.title);
    setEditingLsStart(toDatetimeLocal(ls.startTime));
    setEditingLsEnd(toDatetimeLocal(ls.endTime));
    setShowAddLs(false);
  };

  const cancelEditLs = () => {
    setEditingLsId(null);
    setEditingLsTitle('');
    setEditingLsStart('');
    setEditingLsEnd('');
  };

  const saveLs = async (lsId: number) => {
    const trimmed = editingLsTitle.trim();
    if (!trimmed) {
      setError('Tên buổi học không được để trống');
      return;
    }
    if (!editingLsStart || !editingLsEnd) {
      setError('Vui lòng nhập đầy đủ ngày/giờ bắt đầu và kết thúc');
      return;
    }
    const startIso = toDbDateTimePayload(editingLsStart);
    const endIso = toDbDateTimePayload(editingLsEnd);
    if (!startIso || !endIso) {
      setError('Định dạng ngày/giờ không hợp lệ');
      return;
    }
    if (endIso <= startIso) {
      setError('Giờ kết thúc phải sau giờ bắt đầu');
      return;
    }

    setSavingLs(true);
    setError(null);
    try {
      const res = await fetch(`/api/v1/admin/live-sessions/${lsId}`, {
        method: 'PUT',
        headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ title: trimmed, startTime: startIso, endTime: endIso }),
      });
      const data = await res.json();
      if (data.success) {
        setLiveSessions(prev =>
          prev
            .map(ls => (ls.id === lsId ? { ...ls, ...data.data, title: data.data.title ?? trimmed } : ls))
            .sort((a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime())
        );
        cancelEditLs();
        fetchClassrooms();
        if (detailClassroomId) refreshDetail(detailClassroomId);
      } else {
        setError(data.message || 'Lỗi khi cập nhật buổi học');
      }
    } catch {
      setError('Không thể kết nối server');
    } finally {
      setSavingLs(false);
    }
  };

  const openAddLs = () => {
    setShowAddLs(true);
    setEditingLsId(null);
    setAddLsForm({ title: '', startTime: '', endTime: '', autoSchedule: true });
  };

  const handleAddLs = async (classroomId: number) => {
    setAddingLs(true);
    setError(null);
    try {
      const body: Record<string, unknown> = {};
      if (addLsForm.title.trim()) body.title = addLsForm.title.trim();
      if (!addLsForm.autoSchedule) {
        if (!addLsForm.startTime || !addLsForm.endTime) {
          setError('Vui lòng nhập ngày/giờ bắt đầu và kết thúc, hoặc chọn tự tính theo lịch tuần');
          setAddingLs(false);
          return;
        }
        body.startTime = toDbDateTimePayload(addLsForm.startTime);
        body.endTime = toDbDateTimePayload(addLsForm.endTime);
      }

      const res = await fetch(`/api/v1/admin/classrooms/${classroomId}/live-sessions`, {
        method: 'POST',
        headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (data.success) {
        setShowAddLs(false);
        await fetchLiveSessions(classroomId);
        fetchClassrooms();
        if (detailClassroomId === classroomId) refreshDetail(classroomId);
      } else {
        setError(data.message || 'Lỗi khi thêm buổi học');
      }
    } catch {
      setError('Không thể kết nối server');
    } finally {
      setAddingLs(false);
    }
  };

  const handleCancelAndReschedule = async (ls: LiveSession) => {
    const ok = window.confirm(
      `Xác nhận nghỉ buổi "${ls.title}"?\n\n` +
      `• Buổi này sẽ đánh dấu NGHỈ\n` +
      `• Tên/nội dung sẽ được gán lại cho các buổi tiếp theo\n` +
      `• Hệ thống tự tạo thêm 1 buổi bù theo lịch tuần của lớp`
    );
    if (!ok) return;

    setCancellingLsId(ls.id);
    setError(null);
    try {
      const res = await fetch(`/api/v1/admin/live-sessions/${ls.id}/cancel-and-reschedule`, {
        method: 'POST',
        headers: getAuthHeader(),
      });
      const data = await res.json();
      if (data.success) {
        setLiveSessions(data.data || []);
        setEditingLsId(null);
        alert(data.message || 'Đã xác nhận nghỉ và cập nhật lịch.');
        fetchClassrooms();
        if (detailClassroomId) refreshDetail(detailClassroomId);
      } else {
        setError(data.message || 'Lỗi khi xác nhận nghỉ buổi');
      }
    } catch {
      setError('Không thể kết nối server');
    } finally {
      setCancellingLsId(null);
    }
  };

  const [generatingFromCourse, setGeneratingFromCourse] = useState(false);

  const handleGenerateSessionsFromCourse = async (classroomId: number, forceOverwrite = false) => {
    if (!detailClassroom) return;

    if (liveSessions.length > 0 && !forceOverwrite) {
      const ok = window.confirm(
        `Lớp "${detailClassroom.name}" hiện đã có ${liveSessions.length} buổi học.\n\n` +
        `Bạn có muốn XÓA và SINH LẠI TOÀN BỘ các buổi học theo danh mục của khóa học không?\n` +
        `(Lưu ý: Chỉ thực hiện được nếu chưa có buổi học nào có video bài giảng).`
      );
      if (!ok) return;
      return handleGenerateSessionsFromCourse(classroomId, true);
    }

    if (liveSessions.length === 0) {
      const ok = window.confirm(
        `Xác nhận tự động tạo các buổi học cho lớp "${detailClassroom.name}"?\n\n` +
        `• Số buổi và nội dung sẽ lấy theo các buổi mẫu của khóa học\n` +
        `• Ngày/giờ học từng buổi sẽ được tính tự động theo lịch học tuần và ngày bắt đầu của lớp.`
      );
      if (!ok) return;
    }

    setGeneratingFromCourse(true);
    setError(null);
    try {
      const res = await fetch(`/api/v1/admin/classrooms/${classroomId}/generate-sessions-from-course`, {
        method: 'POST',
        headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ overwrite: forceOverwrite }),
      });
      const data = await res.json();
      if (data.success) {
        alert(data.message || 'Đã tạo thành công các buổi học theo khóa học!');
        await fetchLiveSessions(classroomId);
        fetchClassrooms();
        if (detailClassroomId === classroomId) refreshDetail(classroomId);
      } else {
        setError(data.message || 'Lỗi khi tạo buổi học theo khóa học');
      }
    } catch {
      setError('Không thể kết nối server');
    } finally {
      setGeneratingFromCourse(false);
    }
  };

  // ── Form helpers ─────────────────────────────
  const openCreate = () => {
    setEditingId(null);
    setError(null);
    setForm({ ...emptyForm, scheduleItems: [{ ...emptySchedule }] });
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    setEditingId(null);
  };

  const openEdit = (c: Classroom) => {
    setEditingId(c.id);
    setError(null);
    setForm({
      name: c.name,
      code: c.code || '',
      startDate: c.startDate ? c.startDate.slice(0, 10) : '',
      startEnrollmentDate: c.startEnrollmentDate ? c.startEnrollmentDate.slice(0, 10) : '',
      totalSessions: c.totalSessions || 12,
      fee: c.fee != null ? String(c.fee) : '',
      totalSlot: c.totalSlot != null ? String(c.totalSlot) : '',
      zaloLink: c.zaloLink || '',
      address: c.address || '',
      viewFromClassroomId: c.viewFromClassroomId != null ? String(c.viewFromClassroomId) : '',
      scheduleItems: parseSchedule(c.scheduleItems).length > 0
        ? parseSchedule(c.scheduleItems)
        : [{ ...emptySchedule }],
    });
    setShowForm(true);
  };

  const handleScheduleChange = (idx: number, field: keyof ScheduleItem, value: any) => {
    setForm(f => {
      const items = [...f.scheduleItems];
      items[idx] = { ...items[idx], [field]: field === 'dayOfWeek' ? Number(value) : value };
      return { ...f, scheduleItems: items };
    });
  };

  const addScheduleRow = () => {
    setForm(f => ({ ...f, scheduleItems: [...f.scheduleItems, { ...emptySchedule }] }));
  };

  const removeScheduleRow = (idx: number) => {
    setForm(f => ({ ...f, scheduleItems: f.scheduleItems.filter((_, i) => i !== idx) }));
  };

  // ── Save classroom ────────────────────────────
  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      const url = editingId
        ? `/api/v1/admin/classrooms/${editingId}`
        : `/api/v1/admin/courses/${courseId}/classrooms`;
      const method = editingId ? 'PUT' : 'POST';

      const body = {
        name: form.name,
        code: form.code || null,
        startDate: form.startDate,
        startEnrollmentDate: form.startEnrollmentDate || null,
        totalSessions: Number(form.totalSessions),
        scheduleItems: form.scheduleItems,
        fee: form.fee ? Number(form.fee) : null,
        totalSlot: form.totalSlot ? Number(form.totalSlot) : null,
        zaloLink: form.zaloLink || null,
        address: form.address || null,
        viewFromClassroomId: form.viewFromClassroomId ? Number(form.viewFromClassroomId) : null,
      };

      const res = await fetch(url, {
        method,
        headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (data.success) {
        const savedId = editingId;
        await fetchClassrooms();
        setShowForm(false);
        setEditingId(null);
        if (savedId && detailClassroomId === savedId) {
          await refreshDetail(savedId);
        }
      } else {
        setError(data.message || 'Lỗi khi lưu lớp học');
      }
    } catch {
      setError('Không thể kết nối server');
    } finally {
      setSaving(false);
    }
  };

  // ── Toggle active ─────────────────────────────
  const handleToggleActive = async (c: Classroom) => {
    try {
      const res = await fetch(`/api/v1/admin/classrooms/${c.id}/toggle-active`, {
        method: 'PATCH',
        headers: getAuthHeader(),
      });
      const data = await res.json();
      if (data.success) {
        const isActive = data.data.isActive ?? data.data.is_active;
        setClassrooms(prev => prev.map(cl => cl.id === c.id ? { ...cl, isActive } : cl));
        if (detailClassroom?.id === c.id) {
          setDetailClassroom(prev => (prev ? { ...prev, isActive } : prev));
        }
      }
    } catch { /* ignore */ }
  };

  // ── Create Meet ───────────────────────────────
  const handleCreateMeet = async (classroomId: number) => {
    if (!window.confirm('Bạn có chắc muốn tạo chung 1 link Google Meet cho toàn bộ các buổi học của lớp này?')) return;
    setCreatingMeet(classroomId);
    try {
      const res = await fetch(`/api/v1/admin/classrooms/${classroomId}/create-meet`, {
        method: 'POST',
        headers: { ...getAuthHeader(), 'Content-Type': 'application/json' }
      });
      const data = await res.json();
      if (data.success) {
        alert(`✅ Tạo Google Meet thành công!\n${data.data.meetLink ? `Meet link: ${data.data.meetLink}` : '⚠️ Không tạo được Meet link'}\nSố học viên được mời: ${data.meta?.attendeeCount ?? 0}`);
        fetchClassrooms();
        if (detailClassroomId === classroomId) {
          await refreshDetail(classroomId);
        } else if (expandedClassroom === classroomId) {
          fetchLiveSessions(classroomId);
        }
      } else {
        alert(data.message || 'Tạo Meet thất bại');
      }
    } catch {
      alert('Lỗi kết nối server');
    } finally {
      setCreatingMeet(null);
    }
  };

  const openAssignHomework = async (classroom: Classroom) => {
    setHwModalClassroom(classroom);
    setHwLoading(true);
    setHwOptions([]);
    setHwSelectedIds(new Set());
    try {
      const res = await fetch(`/api/v1/admin/classrooms/${classroom.id}/homeworks`, {
        headers: getAuthHeader(),
      });
      const data = await res.json();
      if (data.success) {
        const list = data.data?.homeworks || [];
        setHwOptions(list);
        setHwSelectedIds(
          new Set(
            list.filter((h: any) => Number(h.assigned) === 1).map((h: any) => Number(h.id))
          )
        );
      } else {
        alert(data.message || 'Không tải được BTVN');
        setHwModalClassroom(null);
      }
    } catch {
      alert('Lỗi kết nối server');
      setHwModalClassroom(null);
    } finally {
      setHwLoading(false);
    }
  };

  const saveAssignHomework = async () => {
    if (!hwModalClassroom) return;
    setHwSaving(true);
    try {
      const res = await fetch(`/api/v1/admin/classrooms/${hwModalClassroom.id}/homeworks`, {
        method: 'PUT',
        headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ homeworkIds: [...hwSelectedIds] }),
      });
      const data = await res.json();
      if (data.success) {
        alert('Đã cập nhật BTVN cho lớp');
        setHwModalClassroom(null);
      } else {
        alert(data.message || 'Gán BTVN thất bại');
      }
    } catch {
      alert('Lỗi kết nối server');
    } finally {
      setHwSaving(false);
    }
  };

  const openPreview = async (session: LiveSession) => {
    setPreviewSession(session);
    setPreviewVideoUrl(null);
    setPreviewFormat('mp4');
    try {
      const res = await fetch(
        `/api/v1/student/playback/token?type=recording&id=${session.id}`,
        { headers: getAuthHeader() }
      );
      const data = await res.json();
      if (data.success) {
        const format = resolvePlaybackFormat(data.data.format);
        setPreviewFormat(format);
        setPreviewVideoUrl(buildPlaybackStreamUrl('/api/v1', data.data.playbackToken, format));
      } else {
        setPreviewVideoUrl(null);
      }
    } catch (error) {
      console.error('Failed to load playback token', error);
      setPreviewVideoUrl(null);
    }
  };

  // ─────────────────────────────────────────────
  // Render
  // ─────────────────────────────────────────────
  return (
    <div className="bg-m2-surface-container rounded-lg shadow-sm border border-m2-outline-variant">
      {/* Header */}
      <div className="px-6 py-4 border-b border-m2-outline-variant flex items-center justify-between">
        <div>
          <h2 className="text-lg font-bold text-m2-on-surface">🏫 Quản lý Lớp học</h2>
          <p className="text-sm text-m2-on-surface-variant mt-0.5">Tạo và quản lý các lớp học, lịch học, live class</p>
        </div>
        <button
          onClick={openCreate}
          className="flex items-center space-x-2 bg-m2-primary hover:bg-eh-forest-shadow text-white text-sm font-semibold px-4 py-2 rounded-lg transition-colors shadow-sm"
        >
          <span>＋ Tạo lớp học</span>
        </button>
      </div>

      {error && !showForm && (
        <div className="mx-6 mt-4 bg-m2-error/10 border border-m2-error/30 text-red-700 text-sm p-3 rounded-lg">
          ⚠️ {error}
        </div>
      )}

      {/* ── Form tạo / sửa lớp học ─────────────────────────── */}
      {showForm && (
        <div className="fixed inset-0 z-50 overflow-y-auto bg-black/50 p-4">
          <div className="mx-auto my-6 w-full max-w-3xl rounded-xl border border-m2-primary/30 bg-m2-surface-container p-5 shadow-2xl">
          <div className="flex items-start justify-between mb-4">
            <h3 className="font-bold text-m2-on-surface">
              {editingId ? '✏️ Chỉnh sửa lớp học' : '➕ Tạo lớp học mới'}
            </h3>
            <button type="button" onClick={closeForm}
              className="text-m2-on-surface-variant hover:text-m2-on-surface w-8 h-8 rounded-lg hover:bg-m2-surface-high transition-colors"
            >✕</button>
          </div>

          {error && (
            <div className="mb-4 bg-m2-error/10 border border-m2-error/30 text-red-700 text-sm p-3 rounded-lg">
              ⚠️ {error}
            </div>
          )}

          <form onSubmit={handleSave} className="space-y-4">
            {/* Row: code + name */}
            <div className="grid grid-cols-12 gap-3">
              <div className="col-span-3">
                <label className="block text-xs font-semibold text-m2-on-surface mb-1">Mã lớp</label>
                <input type="text" value={form.code}
                  onChange={e => setForm(f => ({ ...f, code: e.target.value }))}
                  placeholder="VD: MBA01-2026"
                  className="w-full border border-m2-outline rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-m2-primary"
                />
              </div>
              <div className="col-span-9">
                <label className="block text-xs font-semibold text-m2-on-surface mb-1">Tên lớp học <span className="text-red-500">*</span></label>
                <input type="text" value={form.name} required
                  onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                  placeholder="VD: Lớp MBA Tháng 8/2026"
                  className="w-full border border-m2-outline rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-m2-primary"
                />
              </div>
            </div>

            {/* Row: dates + sessions */}
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="block text-xs font-semibold text-m2-on-surface mb-1">📅 Ngày bắt đầu tuyển sinh</label>
                <input type="date" value={form.startEnrollmentDate}
                  onChange={e => setForm(f => ({ ...f, startEnrollmentDate: e.target.value }))}
                  className="w-full border border-m2-outline rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-m2-primary"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-m2-on-surface mb-1">🎓 Ngày bắt đầu học <span className="text-red-500">*</span></label>
                <input type="date" value={form.startDate} required
                  onChange={e => setForm(f => ({ ...f, startDate: e.target.value }))}
                  className="w-full border border-m2-outline rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-m2-primary"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-m2-on-surface mb-1">🔢 Số buổi học <span className="text-red-500">*</span></label>
                <input type="number" min={1} value={form.totalSessions} required
                  onChange={e => setForm(f => ({ ...f, totalSessions: Number(e.target.value) }))}
                  className="w-full border border-m2-outline rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-m2-primary"
                />
                <p className="text-[10px] text-m2-primary mt-1">Hệ thống sẽ tự tính ngày kết thúc</p>
              </div>
            </div>

            {/* Schedule */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-xs font-semibold text-m2-on-surface">🗓️ Lịch học hàng tuần <span className="text-red-500">*</span></label>
                <button type="button" onClick={addScheduleRow}
                  className="text-xs text-m2-primary hover:text-m2-primary font-medium"
                >＋ Thêm ca học</button>
              </div>
              <div className="space-y-2">
                {form.scheduleItems.map((item, idx) => (
                  <div key={idx} className="flex items-center gap-2 bg-m2-surface-container border border-m2-outline-variant rounded-lg px-3 py-2">
                    <select value={item.dayOfWeek}
                      onChange={e => handleScheduleChange(idx, 'dayOfWeek', e.target.value)}
                      className="border border-m2-outline rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-m2-primary"
                    >
                      {[1,2,3,4,5,6,7].map(d => (
                        <option key={d} value={d}>{DAY_LABELS[d]}</option>
                      ))}
                    </select>
                    <span className="text-xs text-m2-on-surface-variant">từ</span>
                    <input type="time" value={item.startHour}
                      onChange={e => handleScheduleChange(idx, 'startHour', e.target.value)}
                      className="border border-m2-outline rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-m2-primary w-28"
                    />
                    <span className="text-xs text-m2-on-surface-variant">đến</span>
                    <input type="time" value={item.endHour}
                      onChange={e => handleScheduleChange(idx, 'endHour', e.target.value)}
                      className="border border-m2-outline rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-m2-primary w-28"
                    />
                    {form.scheduleItems.length > 1 && (
                      <button type="button" onClick={() => removeScheduleRow(idx)}
                        className="text-red-500 hover:text-red-700 ml-auto text-xs"
                      >✕</button>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Row: fee + slot */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-semibold text-m2-on-surface mb-1">💰 Học phí (VNĐ)</label>
                <input type="number" min={0} value={form.fee}
                  onChange={e => setForm(f => ({ ...f, fee: e.target.value }))}
                  placeholder="0"
                  className="w-full border border-m2-outline rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-m2-primary"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-m2-on-surface mb-1">👥 Số chỗ</label>
                <input type="number" min={1} value={form.totalSlot}
                  onChange={e => setForm(f => ({ ...f, totalSlot: e.target.value }))}
                  placeholder="20"
                  className="w-full border border-m2-outline rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-m2-primary"
                />
              </div>
            </div>

            {/* viewFromClassroomId */}
            {classrooms.filter(c => c.id !== editingId).length > 0 && (
              <div>
                <label className="block text-xs font-semibold text-m2-on-surface mb-1">
                  📹 Cho phép xem video live class của lớp (tùy chọn)
                </label>
                <select value={form.viewFromClassroomId}
                  onChange={e => setForm(f => ({ ...f, viewFromClassroomId: e.target.value }))}
                  className="w-full border border-m2-outline rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-m2-primary bg-m2-surface-container"
                >
                  <option value="">-- Không xem thêm lớp khác --</option>
                  {classrooms.filter(c => c.id !== editingId).map(c => (
                    <option key={c.id} value={c.id}>{c.name} ({c.code || `#${c.id}`})</option>
                  ))}
                </select>
                <p className="text-[11px] text-m2-primary mt-1">
                  💡 Học viên lớp này sẽ xem được video record buổi học của lớp được chọn
                </p>
              </div>
            )}

            {/* address + zalo */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-semibold text-m2-on-surface mb-1">📍 Địa điểm / Link Online</label>
                <input type="text" value={form.address}
                  onChange={e => setForm(f => ({ ...f, address: e.target.value }))}
                  placeholder="VD: Online / 123 Nguyễn Văn X"
                  className="w-full border border-m2-outline rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-m2-primary"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-m2-on-surface mb-1">💬 Zalo Group Link</label>
                <input type="text" value={form.zaloLink}
                  onChange={e => setForm(f => ({ ...f, zaloLink: e.target.value }))}
                  placeholder="https://zalo.me/g/..."
                  className="w-full border border-m2-outline rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-m2-primary"
                />
              </div>
            </div>

            <div className="flex items-center space-x-3 pt-1">
              <button type="submit" disabled={saving}
                className="bg-m2-primary hover:bg-eh-forest-shadow text-white text-sm font-semibold px-5 py-2 rounded-lg transition-colors disabled:opacity-50"
              >
                {saving ? '⏳ Đang lưu...' : (editingId ? 'Lưu thay đổi' : 'Tạo lớp học')}
              </button>
              <button type="button" onClick={closeForm}
                className="text-sm text-m2-on-surface-variant hover:text-m2-on-surface px-4 py-2 rounded-lg hover:bg-m2-surface-high transition-colors"
              >
                Hủy
              </button>
            </div>
          </form>
          </div>
        </div>
      )}

      {/* ── Chi tiết lớp học ────────────────────────────── */}
      {detailClassroomId != null ? (
        <div className="p-6 space-y-4">
          <button
            type="button"
            onClick={closeDetail}
            className="text-sm font-semibold text-m2-primary hover:underline flex items-center gap-1"
          >
            ← Quay lại danh sách lớp
          </button>

          {loadingDetail && !detailClassroom ? (
            <div className="text-center py-12 text-m2-outline">
              <div className="inline-block w-6 h-6 border-2 border-m2-outline border-t-blue-600 rounded-full animate-spin mb-2" />
              <p className="text-sm">Đang tải chi tiết lớp...</p>
            </div>
          ) : detailClassroom ? (
            <>
              {/* Header + actions */}
              <div className="border border-m2-outline-variant rounded-xl p-5 bg-m2-surface-container">
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      {detailClassroom.code && (
                        <span className="text-[11px] font-mono bg-m2-surface-high text-m2-on-surface-variant px-2 py-0.5 rounded">
                          {detailClassroom.code}
                        </span>
                      )}
                      <h3 className="text-xl font-bold text-m2-on-surface">{detailClassroom.name}</h3>
                      <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${classroomStatusMeta(detailClassroom).color}`}>
                        {classroomStatusMeta(detailClassroom).label}
                      </span>
                      {!detailClassroom.isActive && (
                        <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-red-100 text-red-600">
                          🔴 Ngừng hoạt động
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-m2-on-surface-variant">
                      👥 {detailClassroom.totalRegistered ?? 0}/{detailClassroom.totalSlot ?? '∞'} học viên
                      {' · '}📚 {detailClassroom.totalSessions ?? '—'} buổi (mẫu)
                      {' · '}📅 {formatDate(detailClassroom.startDate)} → {formatDate(detailClassroom.endDate)}
                    </p>
                  </div>

                  <div className="flex flex-wrap items-center gap-2">
                    <button
                      type="button"
                      onClick={() => void openAssignHomework(detailClassroom)}
                      className="text-xs font-semibold px-3 py-2 rounded-lg bg-amber-50 hover:bg-amber-100 text-amber-800 border border-amber-200"
                    >
                      📝 Gán BTVN
                    </button>
                    <button
                      type="button"
                      onClick={() => openEdit(detailClassroom)}
                      className="text-xs font-semibold px-3 py-2 rounded-lg bg-m2-primary/10 hover:bg-m2-primary/15 text-m2-primary border border-m2-primary/30"
                    >
                      ✏️ Sửa lớp
                    </button>
                    <button
                      type="button"
                      onClick={() => handleToggleActive(detailClassroom)}
                      className={`text-xs font-semibold px-3 py-2 rounded-lg border ${
                        detailClassroom.isActive
                          ? 'bg-orange-50 hover:bg-orange-100 text-orange-700 border-orange-200'
                          : 'bg-green-50 hover:bg-green-100 text-green-700 border-green-200'
                      }`}
                    >
                      {detailClassroom.isActive ? '🔴 Ngừng hoạt động' : '🟢 Kích hoạt'}
                    </button>
                    <button
                      type="button"
                      onClick={() => handleCreateMeet(detailClassroom.id)}
                      disabled={creatingMeet === detailClassroom.id}
                      className="text-xs font-semibold px-3 py-2 rounded-lg bg-purple-50 hover:bg-purple-100 text-purple-700 border border-purple-200 disabled:opacity-50"
                    >
                      {creatingMeet === detailClassroom.id ? '⏳ Đang tạo Meet...' : '📹 Tạo Google Meet'}
                    </button>
                    {detailClassroom.googleMeetLink && (
                      <a
                        href={detailClassroom.googleMeetLink}
                        target="_blank"
                        rel="noreferrer"
                        className="text-xs font-semibold px-3 py-2 rounded-lg bg-m2-primary text-white hover:bg-eh-forest-shadow"
                      >
                        🔗 Mở Meet
                      </a>
                    )}
                    <button
                      type="button"
                      onClick={() => handleDeleteClassroom(detailClassroom)}
                      disabled={
                        deletingClassroom ||
                        (detailClassroom.totalRegistered ?? 0) > 0 ||
                        detailClassroom.canDelete === false
                      }
                      title={
                        (detailClassroom.totalRegistered ?? 0) > 0 || detailClassroom.canDelete === false
                          ? 'Chỉ xóa được khi chưa có học viên đăng ký'
                          : 'Xóa lớp học'
                      }
                      className="text-xs font-semibold px-3 py-2 rounded-lg bg-red-50 hover:bg-red-100 text-red-700 border border-red-200 disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      {deletingClassroom ? '⏳ Đang xóa...' : '🗑️ Xóa lớp'}
                    </button>
                  </div>
                </div>

                {/* Tabs */}
                <div className="mt-5 flex gap-1 border-b border-m2-outline-variant">
                  {(
                    [
                      { id: 'info' as const, label: '📋 Thông tin' },
                      { id: 'students' as const, label: `👥 Học viên (${students.filter(s => !isCancelledReg(s.status)).length})` },
                      { id: 'sessions' as const, label: `📹 Buổi học (${liveSessions.length})` },
                    ]
                  ).map(tab => (
                    <button
                      key={tab.id}
                      type="button"
                      onClick={() => setDetailTab(tab.id)}
                      className={`px-4 py-2.5 text-sm font-semibold border-b-2 -mb-px transition-colors ${
                        detailTab === tab.id
                          ? 'border-m2-primary text-m2-primary'
                          : 'border-transparent text-m2-on-surface-variant hover:text-m2-on-surface'
                      }`}
                    >
                      {tab.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Tab: info */}
              {detailTab === 'info' && (
                <div className="border border-m2-outline-variant rounded-xl p-5 bg-m2-surface-container space-y-4">
                  <h4 className="text-sm font-bold text-m2-on-surface uppercase tracking-wide">Thông tin cơ bản</h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 text-sm">
                    <div>
                      <p className="text-xs text-m2-on-surface-variant mb-0.5">Mã lớp</p>
                      <p className="font-semibold text-m2-on-surface">{detailClassroom.code || '—'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-m2-on-surface-variant mb-0.5">Trạng thái</p>
                      <p className="font-semibold text-m2-on-surface">{classroomStatusMeta(detailClassroom).label}</p>
                    </div>
                    <div>
                      <p className="text-xs text-m2-on-surface-variant mb-0.5">Ngày bắt đầu tuyển sinh</p>
                      <p className="font-semibold text-m2-on-surface">{formatDate(detailClassroom.startEnrollmentDate)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-m2-on-surface-variant mb-0.5">Ngày bắt đầu học</p>
                      <p className="font-semibold text-m2-on-surface">{formatDate(detailClassroom.startDate)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-m2-on-surface-variant mb-0.5">Ngày kết thúc</p>
                      <p className="font-semibold text-m2-on-surface">{formatDate(detailClassroom.endDate)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-m2-on-surface-variant mb-0.5">Số buổi / chỗ</p>
                      <p className="font-semibold text-m2-on-surface">
                        {detailClassroom.totalSessions ?? '—'} buổi · {detailClassroom.totalRegistered ?? 0}/{detailClassroom.totalSlot ?? '∞'} HV
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-m2-on-surface-variant mb-0.5">Học phí</p>
                      <p className="font-semibold text-m2-on-surface">{formatMoney(detailClassroom.fee)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-m2-on-surface-variant mb-0.5">Địa điểm / Online</p>
                      <p className="font-semibold text-m2-on-surface break-all">{detailClassroom.address || '—'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-m2-on-surface-variant mb-0.5">Zalo group</p>
                      {detailClassroom.zaloLink ? (
                        <a href={detailClassroom.zaloLink} target="_blank" rel="noreferrer" className="font-semibold text-m2-primary hover:underline break-all">
                          {detailClassroom.zaloLink}
                        </a>
                      ) : (
                        <p className="font-semibold text-m2-on-surface">—</p>
                      )}
                    </div>
                    <div>
                      <p className="text-xs text-m2-on-surface-variant mb-0.5">Google Meet</p>
                      {detailClassroom.googleMeetLink ? (
                        <a href={detailClassroom.googleMeetLink} target="_blank" rel="noreferrer" className="font-semibold text-m2-primary hover:underline break-all">
                          {detailClassroom.googleMeetLink}
                        </a>
                      ) : (
                        <p className="font-semibold text-m2-on-surface">Chưa tạo</p>
                      )}
                    </div>
                    <div>
                      <p className="text-xs text-m2-on-surface-variant mb-0.5">Xem video lớp khác</p>
                      <p className="font-semibold text-m2-on-surface">{detailClassroom.viewFromClassroomName || 'Không'}</p>
                    </div>
                  </div>

                  {parseSchedule(detailClassroom.scheduleItems).length > 0 && (
                    <div>
                      <p className="text-xs text-m2-on-surface-variant mb-2">Lịch học tuần</p>
                      <div className="flex flex-wrap gap-1.5">
                        {parseSchedule(detailClassroom.scheduleItems).map((s, i) => (
                          <span key={i} className="text-xs bg-m2-primary/10 text-m2-primary border border-m2-primary/30 rounded-full px-2.5 py-1">
                            {DAY_LABELS[s.dayOfWeek]} {s.startHour}–{s.endHour}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {(detailClassroom.totalRegistered ?? 0) === 0 && detailClassroom.canDelete !== false && (
                    <p className="text-xs text-m2-on-surface-variant bg-m2-surface-low border border-m2-outline-variant rounded-lg px-3 py-2">
                      💡 Lớp chưa có đăng ký — có thể xóa lớp bằng nút <strong>Xóa lớp</strong>.
                    </p>
                  )}
                </div>
              )}

              {/* Tab: students */}
              {detailTab === 'students' && (
                <div className="border border-m2-outline-variant rounded-xl bg-m2-surface-container overflow-hidden">
                  <div className="px-5 py-3 border-b border-m2-outline-variant flex items-center justify-between gap-2">
                    <p className="text-sm font-bold text-m2-on-surface">Danh sách học viên trong lớp</p>
                    <button
                      type="button"
                      onClick={() => detailClassroomId && fetchStudents(detailClassroomId)}
                      className="text-xs text-m2-primary hover:underline"
                    >
                      Làm mới
                    </button>
                  </div>
                  {loadingStudents ? (
                    <div className="flex justify-center py-10">
                      <div className="w-5 h-5 border-2 border-m2-outline border-t-blue-600 rounded-full animate-spin" />
                    </div>
                  ) : students.length === 0 ? (
                    <p className="text-sm text-m2-on-surface-variant text-center py-10">
                      Chưa có học viên nào đăng ký lớp này.
                    </p>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="bg-m2-surface-low text-left text-xs text-m2-on-surface-variant">
                            <th className="px-4 py-2.5 font-semibold">Họ tên</th>
                            <th className="px-4 py-2.5 font-semibold">Email</th>
                            <th className="px-4 py-2.5 font-semibold">SĐT</th>
                            <th className="px-4 py-2.5 font-semibold">Mã ĐK</th>
                            <th className="px-4 py-2.5 font-semibold">Trạng thái</th>
                            <th className="px-4 py-2.5 font-semibold">Học phí</th>
                            <th className="px-4 py-2.5 font-semibold">Ngày ĐK</th>
                            <th className="px-4 py-2.5 font-semibold text-right">Thao tác</th>
                          </tr>
                        </thead>
                        <tbody>
                          {students.map(st => (
                            <tr
                              key={st.registerId}
                              className={`border-t border-m2-outline-variant ${isCancelledReg(st.status) ? 'opacity-60' : ''}`}
                            >
                              <td className="px-4 py-2.5 font-medium text-m2-on-surface">{st.fullName || '—'}</td>
                              <td className="px-4 py-2.5 text-m2-on-surface-variant">{st.email || '—'}</td>
                              <td className="px-4 py-2.5 text-m2-on-surface-variant">{st.phone || '—'}</td>
                              <td className="px-4 py-2.5 font-mono text-xs">{st.registerCode || '—'}</td>
                              <td className="px-4 py-2.5">
                                <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${REG_STATUS_COLORS[String(st.status || '').toUpperCase()] || 'bg-m2-surface-high text-m2-on-surface-variant'}`}>
                                  {st.status || '—'}
                                </span>
                              </td>
                              <td className="px-4 py-2.5">{formatMoney(st.fee)}</td>
                              <td className="px-4 py-2.5 text-m2-on-surface-variant whitespace-nowrap">
                                {st.registerAt ? new Date(st.registerAt).toLocaleString('vi-VN') : '—'}
                              </td>
                              <td className="px-4 py-2.5 text-right">
                                {!isCancelledReg(st.status) &&
                                  String(st.status || '').toUpperCase().trim() !== 'WAIT_PAYMENT' && (
                                  <button
                                    type="button"
                                    onClick={() => openTransferModal(st)}
                                    className="text-xs font-semibold text-m2-primary hover:underline whitespace-nowrap"
                                  >
                                    Chuyển lớp
                                  </button>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}

              {/* Tab: sessions */}
              {detailTab === 'sessions' && (
                <div className="border border-m2-outline-variant rounded-xl bg-m2-surface-container p-4">
                  <div className="flex items-center justify-between mb-3 gap-2">
                    <div>
                      <p className="text-sm font-bold text-m2-on-surface">Buổi học (Live Sessions)</p>
                      <p className="text-[11px] text-m2-on-surface-variant mt-0.5">
                        Mở từng buổi → <strong>📁 Tài liệu</strong> → chọn file từ danh mục khóa học (tab 📁 Tài liệu trên khóa).
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => detailClassroom && handleGenerateSessionsFromCourse(detailClassroom.id)}
                        disabled={generatingFromCourse}
                        className="text-xs font-semibold text-emerald-700 bg-emerald-50 hover:bg-emerald-100 border border-emerald-200 px-3 py-1.5 rounded-lg transition-colors flex items-center gap-1.5 disabled:opacity-50"
                        title="Tự động tạo các buổi học dựa trên danh mục buổi của khóa học và lịch học tuần của lớp"
                      >
                        {generatingFromCourse ? (
                          <>
                            <div className="w-3.5 h-3.5 border-2 border-emerald-600 border-t-transparent rounded-full animate-spin" />
                            <span>Đang tạo...</span>
                          </>
                        ) : (
                          <>
                            <span>⚡</span>
                            <span>Tạo theo khóa học</span>
                          </>
                        )}
                      </button>
                      <button
                        type="button"
                        onClick={openAddLs}
                        className="text-xs font-semibold text-m2-primary bg-m2-primary/10 hover:bg-m2-primary/15 px-3 py-1.5 rounded-lg transition-colors"
                      >
                        ＋ Thêm buổi lẻ
                      </button>
                    </div>
                  </div>

                  {showAddLs && (
                    <div className="mb-3 border border-m2-primary/30 bg-m2-primary/5 rounded-xl p-3 space-y-3">
                      <p className="text-sm font-semibold text-m2-on-surface">Thêm buổi học phát sinh</p>
                      <div>
                        <label className="block text-[11px] font-semibold text-m2-on-surface mb-1">Tên buổi</label>
                        <input
                          type="text"
                          value={addLsForm.title}
                          onChange={e => setAddLsForm(f => ({ ...f, title: e.target.value }))}
                          placeholder="VD: Buổi bổ sung / Ôn tập"
                          className="w-full border border-m2-outline rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-m2-primary"
                        />
                      </div>
                      <label className="flex items-center gap-2 text-xs text-m2-on-surface cursor-pointer">
                        <input
                          type="checkbox"
                          checked={addLsForm.autoSchedule}
                          onChange={e => setAddLsForm(f => ({ ...f, autoSchedule: e.target.checked }))}
                        />
                        Tự tính ngày/giờ theo lịch tuần của lớp (slot tiếp theo)
                      </label>
                      {!addLsForm.autoSchedule && (
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                          <div>
                            <label className="block text-[11px] font-semibold text-m2-on-surface mb-1">Bắt đầu</label>
                            <input
                              type="datetime-local"
                              value={addLsForm.startTime}
                              onChange={e => setAddLsForm(f => ({ ...f, startTime: e.target.value }))}
                              className="w-full border border-m2-outline rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-m2-primary"
                            />
                          </div>
                          <div>
                            <label className="block text-[11px] font-semibold text-m2-on-surface mb-1">Kết thúc</label>
                            <input
                              type="datetime-local"
                              value={addLsForm.endTime}
                              onChange={e => setAddLsForm(f => ({ ...f, endTime: e.target.value }))}
                              className="w-full border border-m2-outline rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-m2-primary"
                            />
                          </div>
                        </div>
                      )}
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => handleAddLs(detailClassroom.id)}
                          disabled={addingLs}
                          className="text-xs font-semibold bg-m2-primary text-white px-3 py-1.5 rounded-lg hover:bg-eh-forest-shadow disabled:opacity-50"
                        >
                          {addingLs ? '⏳ Đang thêm...' : 'Thêm buổi'}
                        </button>
                        <button
                          type="button"
                          onClick={() => setShowAddLs(false)}
                          disabled={addingLs}
                          className="text-xs text-m2-on-surface-variant px-3 py-1.5 rounded-lg hover:bg-m2-surface-high"
                        >
                          Hủy
                        </button>
                      </div>
                    </div>
                  )}

                  {loadingLS ? (
                    <div className="flex items-center justify-center py-8">
                      <div className="w-5 h-5 border-2 border-m2-outline border-t-blue-600 rounded-full animate-spin" />
                    </div>
                  ) : liveSessions.length === 0 ? (
                    <div className="text-center py-8 px-4 bg-m2-surface-low rounded-xl border border-dashed border-m2-outline-variant my-2">
                      <p className="text-sm font-semibold text-m2-on-surface mb-1">
                        Lớp học chưa có buổi học nào
                      </p>
                      <p className="text-xs text-m2-on-surface-variant max-w-md mx-auto mb-4">
                        Bạn có thể nhấn nút bên dưới để tự động tạo toàn bộ các buổi học theo cấu trúc của khóa học và lịch học tuần của lớp, hoặc thêm từng buổi thủ công.
                      </p>
                      <div className="flex items-center justify-center gap-2 flex-wrap">
                        <button
                          type="button"
                          onClick={() => detailClassroom && handleGenerateSessionsFromCourse(detailClassroom.id)}
                          disabled={generatingFromCourse}
                          className="text-xs font-semibold text-white bg-emerald-600 hover:bg-emerald-700 px-4 py-2 rounded-lg shadow-sm transition-colors flex items-center gap-1.5 disabled:opacity-50"
                        >
                          {generatingFromCourse ? (
                            <>
                              <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                              <span>Đang tạo các buổi học...</span>
                            </>
                          ) : (
                            <>
                              <span>⚡</span>
                              <span>Tạo các buổi học theo khóa học</span>
                            </>
                          )}
                        </button>
                        <button
                          type="button"
                          onClick={openAddLs}
                          className="text-xs font-semibold text-m2-primary bg-m2-primary/10 hover:bg-m2-primary/15 px-3 py-2 rounded-lg transition-colors"
                        >
                          ＋ Thêm buổi lẻ
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {liveSessions.map(ls => {
                        const isCancelled = ls.status === 'CANCELLED';
                        const matCount = Number(ls.materialCount || 0);
                        const materialsOpen = materialsLsId === ls.id;
                        return (
                          <div key={ls.id} className={`bg-m2-surface-low border rounded-lg p-3 ${isCancelled ? 'border-orange-200 opacity-80' : 'border-m2-outline-variant'}`}>
                            <div className="flex items-start justify-between gap-3">
                              <div className="flex-1 min-w-0">
                                {editingLsId === ls.id ? (
                                  <div className="space-y-2">
                                    <div>
                                      <label className="block text-[11px] font-semibold text-m2-on-surface mb-1">Tên buổi</label>
                                      <input
                                        type="text"
                                        value={editingLsTitle}
                                        onChange={e => setEditingLsTitle(e.target.value)}
                                        autoFocus
                                        disabled={savingLs}
                                        className="w-full border border-m2-primary rounded-lg px-2 py-1.5 text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-m2-primary"
                                      />
                                    </div>
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                      <div>
                                        <label className="block text-[11px] font-semibold text-m2-on-surface mb-1">Bắt đầu</label>
                                        <input
                                          type="datetime-local"
                                          value={editingLsStart}
                                          onChange={e => setEditingLsStart(e.target.value)}
                                          disabled={savingLs}
                                          className="w-full border border-m2-outline rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-m2-primary"
                                        />
                                      </div>
                                      <div>
                                        <label className="block text-[11px] font-semibold text-m2-on-surface mb-1">Kết thúc</label>
                                        <input
                                          type="datetime-local"
                                          value={editingLsEnd}
                                          onChange={e => setEditingLsEnd(e.target.value)}
                                          disabled={savingLs}
                                          className="w-full border border-m2-outline rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-m2-primary"
                                        />
                                      </div>
                                    </div>
                                    <div className="flex items-center gap-2">
                                      <button type="button" onClick={() => saveLs(ls.id)} disabled={savingLs}
                                        className="text-[11px] font-semibold bg-m2-primary text-white px-3 py-1.5 rounded-lg hover:bg-eh-forest-shadow disabled:opacity-50">
                                        {savingLs ? '⏳ Đang lưu...' : 'Lưu'}
                                      </button>
                                      <button type="button" onClick={cancelEditLs} disabled={savingLs}
                                        className="text-[11px] text-m2-on-surface-variant px-3 py-1.5 rounded-lg hover:bg-m2-surface-high">
                                        Hủy
                                      </button>
                                    </div>
                                  </div>
                                ) : (
                                  <>
                                    <div className="flex items-center gap-2 flex-wrap">
                                      <span className={`font-semibold text-sm truncate ${isCancelled ? 'text-orange-700 line-through' : 'text-m2-on-surface'}`}>
                                        {ls.title}
                                      </span>
                                      {!isCancelled && (
                                        <button type="button" onClick={() => startEditLs(ls)} title="Sửa tên / ngày / giờ"
                                          className="text-[11px] text-m2-primary hover:bg-m2-primary/10 px-1.5 py-0.5 rounded transition-colors shrink-0">
                                          ✏️
                                        </button>
                                      )}
                                      <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${LS_STATUS_COLORS[ls.status] || 'bg-m2-surface-high'}`}>
                                        {ls.status === 'SCHEDULED' ? '📅 Đã lên lịch'
                                          : ls.status === 'IN_PROGRESS' ? '🔴 Đang diễn ra'
                                          : ls.status === 'COMPLETED' ? '✅ Đã kết thúc'
                                          : '🚫 Nghỉ'}
                                      </span>
                                      {ls.recordingUrl
                                        ? <button type="button" onClick={() => openPreview(ls)}
                                            className="text-[10px] text-purple-600 bg-purple-50 px-1.5 py-0.5 rounded hover:bg-purple-100">🎬 Có video</button>
                                        : !isCancelled && <span className="text-[10px] text-m2-outline">Chưa có video</span>
                                      }
                                      {matCount > 0 && (
                                        <span className="text-[10px] font-semibold text-m2-primary bg-m2-primary/10 px-1.5 py-0.5 rounded">
                                          📁 {matCount} file
                                        </span>
                                      )}
                                    </div>
                                    <p className="text-[11px] text-m2-on-surface-variant mt-0.5">
                                      🕐 {formatDbDateTimeVi(ls.startTime)} → {formatDbDateTimeVi(ls.endTime)}
                                    </p>
                                    {ls.sessionTitle && (
                                      <span className="text-[11px] text-purple-700 bg-purple-50 rounded-full px-2 py-0.5 mt-1 inline-block">
                                        📚 Buổi {ls.sessionOrderIndex}: {ls.sessionTitle}
                                      </span>
                                    )}
                                  </>
                                )}
                              </div>
                              <div className="flex flex-col items-end gap-2 shrink-0">
                                {ls.meetLink && !isCancelled && (
                                  <a href={ls.meetLink} target="_blank" rel="noreferrer" className="text-xs text-m2-primary hover:underline">🔗 Meet</a>
                                )}
                                {!isCancelled && (
                                  <button
                                    type="button"
                                    onClick={() => void toggleMaterialsPanel(ls.id)}
                                    className={`text-[10px] font-semibold px-2 py-1 rounded transition-colors ${
                                      materialsOpen
                                        ? 'bg-m2-primary text-white'
                                        : 'bg-m2-primary/10 hover:bg-m2-primary/15 text-m2-primary'
                                    }`}
                                  >
                                    📁 Tài liệu{matCount > 0 ? ` (${matCount})` : ''}
                                  </button>
                                )}
                                {!isCancelled && !ls.recordingUrl && (
                                  <button onClick={() => openSyncModal(ls.id)}
                                    className="text-[10px] bg-eh-forest-ink hover:bg-eh-forest-shadow text-eh-cream px-2 py-1 rounded transition-colors">
                                    🔄 Đồng bộ
                                  </button>
                                )}
                                {!isCancelled && editingLsId !== ls.id && (
                                  <button type="button" onClick={() => handleCancelAndReschedule(ls)} disabled={cancellingLsId === ls.id}
                                    title="Xác nhận nghỉ buổi — dời nội dung sang buổi sau và tạo buổi bù"
                                    className="text-[10px] font-semibold bg-orange-500/10 hover:bg-orange-100 text-orange-700 px-2 py-1 rounded transition-colors disabled:opacity-50">
                                    {cancellingLsId === ls.id ? '⏳...' : '🚫 Nghỉ buổi'}
                                  </button>
                                )}
                              </div>
                            </div>

                            {materialsOpen && (
                              <div className="mt-3 border-t border-m2-outline-variant pt-3 space-y-3">
                                <div>
                                  <p className="text-xs font-semibold text-m2-on-surface">
                                    Tài liệu gắn buổi (học viên thấy khi mở buổi / recording)
                                  </p>
                                  <p className="text-[11px] text-m2-on-surface-variant mt-0.5">
                                    Chỉ chọn từ danh mục khóa. Upload file mới ở tab <strong>📁 Tài liệu</strong> của khóa học.
                                  </p>
                                </div>

                                {loadingSessionMaterials ? (
                                  <p className="text-xs text-m2-on-surface-variant">Đang tải...</p>
                                ) : (
                                  <>
                                    <div>
                                      <p className="text-[11px] font-semibold text-m2-on-surface mb-1.5">
                                        Đã gắn buổi này ({sessionMaterials.length})
                                      </p>
                                      {sessionMaterials.length === 0 ? (
                                        <p className="text-xs text-m2-on-surface-variant">Chưa gắn tài liệu nào.</p>
                                      ) : (
                                        <ul className="space-y-1.5">
                                          {sessionMaterials.map((mat) => (
                                            <li
                                              key={mat.id}
                                              className="flex items-center justify-between gap-2 rounded-lg border border-m2-primary/30 bg-m2-primary/5 px-2.5 py-1.5"
                                            >
                                              <a
                                                href={mat.fileUrl}
                                                target="_blank"
                                                rel="noreferrer"
                                                className="text-xs text-m2-primary hover:underline truncate min-w-0"
                                              >
                                                {mat.title}
                                                <span className="ml-1.5 text-[10px] uppercase text-m2-on-surface-variant">
                                                  {mat.fileType || 'FILE'}
                                                </span>
                                              </a>
                                              <button
                                                type="button"
                                                disabled={linkingMaterialId === mat.id}
                                                onClick={() => void handleUnlinkMaterial(ls.id, mat.id)}
                                                className="text-[10px] text-orange-700 hover:bg-orange-50 px-1.5 py-0.5 rounded shrink-0 disabled:opacity-50"
                                              >
                                                Gỡ
                                              </button>
                                            </li>
                                          ))}
                                        </ul>
                                      )}
                                    </div>

                                    <div className="rounded-lg border border-m2-outline-variant bg-m2-surface-container p-3 space-y-2">
                                      <p className="text-[11px] font-semibold text-m2-on-surface">
                                        Danh mục tài liệu khóa học
                                      </p>
                                      <input
                                        type="search"
                                        value={materialPickerFilter}
                                        onChange={(e) => setMaterialPickerFilter(e.target.value)}
                                        placeholder="Tìm trong danh mục..."
                                        className="w-full border border-m2-outline rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-m2-primary"
                                      />
                                      {(() => {
                                        const attachedIds = new Set(sessionMaterials.map((m) => m.id));
                                        const q = materialPickerFilter.trim().toLowerCase();
                                        const available = courseCatalogMaterials.filter((m) => {
                                          if (attachedIds.has(m.id)) return false;
                                          if (!q) return true;
                                          return `${m.title || ''} ${m.fileType || ''} ${m.type || ''}`.toLowerCase().includes(q);
                                        });
                                        if (courseCatalogMaterials.length === 0) {
                                          return (
                                            <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-2 py-1.5">
                                              Khóa chưa có tài liệu trong danh mục. Vào tab <strong>📁 Tài liệu</strong> của khóa để upload trước.
                                            </p>
                                          );
                                        }
                                        if (available.length === 0) {
                                          return (
                                            <p className="text-xs text-m2-on-surface-variant">
                                              {q
                                                ? 'Không tìm thấy tài liệu phù hợp.'
                                                : 'Toàn bộ tài liệu danh mục đã gắn buổi này (hoặc không còn file để chọn).'}
                                            </p>
                                          );
                                        }
                                        return (
                                          <ul className="max-h-52 overflow-y-auto space-y-1.5">
                                            {available.map((mat) => (
                                              <li
                                                key={mat.id}
                                                className="flex items-center justify-between gap-2 rounded-lg border border-m2-outline-variant px-2.5 py-1.5 hover:bg-m2-surface-low"
                                              >
                                                <div className="min-w-0">
                                                  <p className="text-xs font-medium text-m2-on-surface truncate">{mat.title}</p>
                                                  <p className="text-[10px] text-m2-on-surface-variant">
                                                    {(mat.fileType || 'FILE').toUpperCase()}
                                                    {mat.sessionTitle
                                                      ? ` · đang gắn buổi ${mat.sessionOrderIndex ?? ''}: ${mat.sessionTitle}`
                                                      : mat.sessionId
                                                        ? ' · đang gắn buổi khác'
                                                        : ' · toàn khóa'}
                                                  </p>
                                                </div>
                                                <button
                                                  type="button"
                                                  disabled={linkingMaterialId === mat.id}
                                                  onClick={() => void handleLinkMaterial(ls.id, mat.id)}
                                                  className="text-[10px] font-semibold bg-m2-primary text-white px-2 py-1 rounded shrink-0 hover:bg-eh-forest-shadow disabled:opacity-50"
                                                >
                                                  {linkingMaterialId === mat.id ? '...' : 'Gắn'}
                                                </button>
                                              </li>
                                            ))}
                                          </ul>
                                        );
                                      })()}
                                    </div>
                                  </>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </>
          ) : (
            <p className="text-sm text-red-600">Không tải được chi tiết lớp.</p>
          )}
        </div>
      ) : (
      /* ── Danh sách lớp học ────────────────────────────── */
      <div className="p-6 space-y-4">
        {loading ? (
          <div className="text-center py-10 text-m2-outline">
            <div className="inline-block w-6 h-6 border-2 border-m2-outline border-t-blue-600 rounded-full animate-spin mb-2" />
            <p className="text-sm">Đang tải...</p>
          </div>
        ) : classrooms.length === 0 ? (
          <div className="text-center py-12 border-2 border-dashed border-m2-outline-variant rounded-xl">
            <div className="text-5xl mb-3">🏫</div>
            <h3 className="font-semibold text-m2-on-surface mb-1">Chưa có lớp học nào</h3>
            <p className="text-sm text-m2-on-surface-variant">Nhấn "Tạo lớp học" để tạo lớp học đầu tiên</p>
          </div>
        ) : (
          classrooms.map(classroom => {
            const schedule = parseSchedule(classroom.scheduleItems);
            const canDelete = (classroom.totalRegistered ?? 0) === 0;
            const statusMeta = classroomStatusMeta(classroom);

            return (
              <div key={classroom.id} className="border border-m2-outline-variant rounded-xl overflow-hidden hover:border-m2-primary/30 transition-colors">
                {/* Classroom row */}
                <div className="p-4 bg-m2-surface-container flex items-start gap-4">
                  <button
                    type="button"
                    onClick={() => void openDetail(classroom.id)}
                    className="flex-1 min-w-0 text-left"
                  >
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      {classroom.code && (
                        <span className="text-[11px] font-mono bg-m2-surface-high text-m2-on-surface-variant px-2 py-0.5 rounded">
                          {classroom.code}
                        </span>
                      )}
                      <h4 className="font-bold text-m2-on-surface hover:text-m2-primary transition-colors">{classroom.name}</h4>
                      <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${statusMeta.color}`}>
                        {statusMeta.label}
                      </span>
                      {!classroom.isActive && (
                        <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-red-100 text-red-600">
                          🔴 Ngừng hoạt động
                        </span>
                      )}
                    </div>

                    <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-m2-on-surface-variant mt-1">
                      <span>📅 Bắt đầu: <strong className="text-m2-on-surface">{formatDate(classroom.startDate)}</strong></span>
                      <span>🏁 Kết thúc: <strong className="text-m2-on-surface">{formatDate(classroom.endDate)}</strong></span>
                      <span>📚 {classroom.totalSessions ?? '—'} buổi</span>
                      <span>👥 {classroom.totalRegistered ?? 0}/{classroom.totalSlot ?? '∞'} học viên</span>
                    </div>

                    {schedule.length > 0 && (
                      <div className="flex flex-wrap gap-1.5 mt-2">
                        {schedule.map((s, i) => (
                          <span key={i} className="text-[11px] bg-m2-primary/10 text-m2-primary border border-m2-primary/30 rounded-full px-2 py-0.5">
                            {DAY_LABELS[s.dayOfWeek]} {s.startHour}–{s.endHour}
                          </span>
                        ))}
                      </div>
                    )}

                    {classroom.viewFromClassroomName && (
                      <div className="mt-1.5 text-[11px] text-purple-700 bg-purple-50 border border-purple-200 rounded-full px-2 py-0.5 inline-flex items-center gap-1">
                        📹 Xem video của: <strong>{classroom.viewFromClassroomName}</strong>
                      </div>
                    )}
                    <p className="text-[11px] text-m2-primary mt-2 font-medium">Nhấn để xem chi tiết →</p>
                  </button>

                  {/* Action icons */}
                  <div className="flex items-center gap-1.5 shrink-0">
                    <button
                      type="button"
                      onClick={() => void openDetail(classroom.id, 'students')}
                      title="Xem học viên / chi tiết lớp"
                      className="w-8 h-8 flex items-center justify-center rounded-lg bg-m2-primary/10 hover:bg-m2-primary/15 text-m2-primary transition-colors text-sm"
                    >👁</button>

                    <button
                      type="button"
                      onClick={() => void openAssignHomework(classroom)}
                      title="Gán BTVN cho lớp"
                      className="w-8 h-8 flex items-center justify-center rounded-lg bg-amber-50 hover:bg-amber-100 text-amber-700 transition-colors text-sm"
                    >📝</button>

                    <button
                      onClick={() => openEdit(classroom)}
                      title="Chỉnh sửa lớp học"
                      className="w-8 h-8 flex items-center justify-center rounded-lg bg-m2-primary/10 hover:bg-m2-primary/15 text-m2-primary transition-colors text-sm"
                    >✏️</button>

                    <button
                      onClick={() => handleToggleActive(classroom)}
                      title={classroom.isActive ? 'Ngừng hoạt động' : 'Kích hoạt'}
                      className={`w-8 h-8 flex items-center justify-center rounded-lg transition-colors text-sm ${classroom.isActive ? 'bg-orange-500/10 hover:bg-orange-100 text-orange-600' : 'bg-green-50 hover:bg-m2-secondary/15 text-m2-secondary'}`}
                    >{classroom.isActive ? '🔴' : '🟢'}</button>

                    <button
                      onClick={() => handleCreateMeet(classroom.id)}
                      disabled={creatingMeet === classroom.id}
                      title="Tạo chung 1 link Google Meet cho lớp"
                      className="w-8 h-8 flex items-center justify-center rounded-lg bg-purple-50 hover:bg-purple-100 text-purple-600 transition-colors text-sm disabled:opacity-50"
                    >{creatingMeet === classroom.id ? '⏳' : '📹'}</button>

                    <button
                      type="button"
                      onClick={() => handleDeleteClassroom(classroom)}
                      disabled={deletingClassroom || !canDelete}
                      title={canDelete ? 'Xóa lớp (chưa có đăng ký)' : 'Không xóa được — lớp đã có học viên đăng ký'}
                      className="w-8 h-8 flex items-center justify-center rounded-lg bg-red-50 hover:bg-red-100 text-red-600 transition-colors text-sm disabled:opacity-40 disabled:cursor-not-allowed"
                    >🗑️</button>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>
      )}

      {/* Sync Record Modal */}
      {syncModalSessionId !== null && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-m2-surface-container rounded-xl shadow-xl w-full max-w-md p-6">
            <h3 className="text-lg font-bold text-m2-on-surface mb-2">🔄 Đồng bộ Video Record</h3>
            <p className="text-sm text-m2-on-surface-variant mb-4">
              Nhập link Google Drive của video hoặc để trống để hệ thống tự động quét và tìm kiếm theo tên lớp học.
            </p>
            <div className="mb-5">
              <label className="block text-xs font-semibold text-m2-on-surface mb-1">Link Google Drive (Tùy chọn)</label>
              <input
                type="text"
                placeholder="https://drive.google.com/file/d/..."
                value={syncLink}
                onChange={e => setSyncLink(e.target.value)}
                className="w-full border border-m2-outline rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-m2-primary"
              />
            </div>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setSyncModalSessionId(null)}
                className="px-4 py-2 text-sm text-m2-on-surface-variant hover:bg-m2-surface-high rounded-lg transition-colors"
                disabled={isSyncing}
              >
                Hủy
              </button>
              <button
                onClick={handleSyncSubmit}
                disabled={isSyncing}
                className="px-4 py-2 text-sm font-semibold text-m2-on-primary bg-m2-primary hover:bg-eh-forest-shadow rounded-lg transition-colors disabled:opacity-50 flex items-center gap-2"
              >
                {isSyncing ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    <span>Đang xử lý...</span>
                  </>
                ) : (
                  <span>{syncLink.trim() ? 'Tải theo Link' : 'Tự động quét'}</span>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {previewSession && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4">
          <div className="bg-m2-surface-container rounded-2xl shadow-2xl w-full max-w-5xl overflow-hidden">
            <div className="flex items-center justify-between px-5 py-4 border-b border-m2-outline-variant">
              <div>
                <h3 className="text-lg font-bold text-m2-on-surface">{previewSession.title}</h3>
                <p className="text-xs text-m2-on-surface-variant">Video record của buổi học</p>
              </div>
              <button
                type="button"
                onClick={() => setPreviewSession(null)}
                className="text-sm text-m2-on-surface-variant hover:text-m2-on-surface px-3 py-1.5 rounded-lg hover:bg-m2-surface-high"
              >
                Đóng
              </button>
            </div>
            <div className="bg-black">
              {previewVideoUrl ? (
                <SecureVideo
                  format={previewFormat}
                  src={previewVideoUrl}
                  videoRef={previewVideoRef}
                  controls
                  autoPlay
                  preload="metadata"
                  className="w-full max-h-[75vh] aspect-video"
                />
              ) : (
                <div className="flex items-center justify-center h-[60vh] text-white">
                  Không có video để phát
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {hwModalClassroom && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <div className="bg-m2-surface-container rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden">
            <div className="flex items-center justify-between px-5 py-4 border-b border-m2-outline-variant">
              <div>
                <h3 className="text-lg font-bold text-m2-on-surface">Gán BTVN cho lớp</h3>
                <p className="text-xs text-m2-on-surface-variant mt-0.5">{hwModalClassroom.name}</p>
              </div>
              <button
                type="button"
                onClick={() => setHwModalClassroom(null)}
                className="text-sm text-m2-on-surface-variant hover:text-m2-on-surface px-3 py-1.5 rounded-lg hover:bg-m2-surface-high"
              >
                Đóng
              </button>
            </div>
            <div className="px-5 py-4 max-h-[60vh] overflow-y-auto">
              {hwLoading ? (
                <p className="text-sm text-m2-on-surface-variant py-6 text-center">Đang tải…</p>
              ) : hwOptions.length === 0 ? (
                <p className="text-sm text-m2-on-surface-variant py-6 text-center">
                  Chưa có BTVN nào trong khóa học. Tạo BTVN ở tab Bài tập về nhà trước.
                </p>
              ) : (
                <ul className="space-y-2">
                  {hwOptions.map((hw) => {
                    const checked = hwSelectedIds.has(hw.id);
                    return (
                      <li key={hw.id}>
                        <label className="flex items-start gap-3 p-3 rounded-xl border border-m2-outline-variant hover:bg-m2-surface-high cursor-pointer">
                          <input
                            type="checkbox"
                            className="mt-1"
                            checked={checked}
                            onChange={() => {
                              setHwSelectedIds((prev) => {
                                const next = new Set(prev);
                                if (next.has(hw.id)) next.delete(hw.id);
                                else next.add(hw.id);
                                return next;
                              });
                            }}
                          />
                          <span className="text-sm text-m2-on-surface font-medium">{hw.name}</span>
                        </label>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
            <div className="flex justify-end gap-2 px-5 py-4 border-t border-m2-outline-variant">
              <button
                type="button"
                onClick={() => setHwModalClassroom(null)}
                className="px-4 py-2 text-sm rounded-lg border border-m2-outline-variant text-m2-on-surface-variant hover:bg-m2-surface-high"
              >
                Hủy
              </button>
              <button
                type="button"
                disabled={hwSaving || hwLoading}
                onClick={() => void saveAssignHomework()}
                className="px-4 py-2 text-sm rounded-lg bg-amber-600 text-white hover:bg-amber-700 disabled:opacity-50"
              >
                {hwSaving ? 'Đang lưu…' : 'Lưu'}
              </button>
            </div>
          </div>
        </div>
      )}

      {transferStudent && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <div className="bg-m2-surface-container rounded-2xl shadow-xl w-full max-w-md border border-m2-outline-variant">
            <div className="px-5 py-4 border-b border-m2-outline-variant">
              <p className="text-sm font-bold text-m2-on-surface">Chuyển lớp học viên</p>
              <p className="text-xs text-m2-on-surface-variant mt-1">
                {transferStudent.fullName || '—'} · {transferStudent.email || '—'}
                {transferStudent.registerCode ? ` · ${transferStudent.registerCode}` : ''}
              </p>
              {detailClassroom && (
                <p className="text-xs text-m2-on-surface-variant mt-0.5">
                  Lớp hiện tại: <strong className="text-m2-on-surface">{detailClassroom.name}</strong>
                  {detailClassroom.code ? ` (${detailClassroom.code})` : ''}
                </p>
              )}
            </div>
            <div className="px-5 py-4 space-y-3">
              <div>
                <label className="block text-[11px] font-semibold text-m2-on-surface mb-1">
                  Chọn lớp đích (cùng khóa)
                </label>
                <select
                  value={transferToId}
                  onChange={(e) => setTransferToId(e.target.value)}
                  className="w-full border border-m2-outline rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-m2-primary bg-m2-surface-container"
                >
                  <option value="">— Chọn lớp —</option>
                  {classrooms
                    .filter((c) => c.id !== detailClassroomId && c.isActive !== false)
                    .map((c) => {
                      const slots =
                        c.totalSlot != null
                          ? `${c.totalRegistered ?? 0}/${c.totalSlot}`
                          : `${c.totalRegistered ?? 0}`;
                      return (
                        <option key={c.id} value={c.id}>
                          {c.name}
                          {c.code ? ` (${c.code})` : ''} · KG {formatDate(c.startDate)} · {slots} HV
                        </option>
                      );
                    })}
                </select>
              </div>
              <div>
                <label className="block text-[11px] font-semibold text-m2-on-surface mb-1">
                  Ghi chú (tuỳ chọn)
                </label>
                <input
                  type="text"
                  value={transferNote}
                  onChange={(e) => setTransferNote(e.target.value)}
                  placeholder="VD: Học viên xin đổi lịch"
                  maxLength={500}
                  className="w-full border border-m2-outline rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-m2-primary"
                />
              </div>
            </div>
            <div className="flex justify-end gap-2 px-5 py-4 border-t border-m2-outline-variant">
              <button
                type="button"
                onClick={closeTransferModal}
                disabled={transferring}
                className="px-4 py-2 text-sm rounded-lg border border-m2-outline-variant text-m2-on-surface-variant hover:bg-m2-surface-high disabled:opacity-50"
              >
                Hủy
              </button>
              <button
                type="button"
                disabled={transferring || !transferToId}
                onClick={() => void handleTransferClassroom()}
                className="px-4 py-2 text-sm rounded-lg bg-m2-primary text-white hover:opacity-90 disabled:opacity-50"
              >
                {transferring ? 'Đang chuyển…' : 'Xác nhận chuyển'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminClassroomManager;
